/**
 * Which stores and which people a pay report covers (plan 0031, owner 2026-10-06). A manager with
 * several stores, such as a franchisee whose staff work shifts in all of them, picks the stores whose
 * hours go in and the people whose pay it is; the accountant then gets one file per employer.
 * The choice is remembered per store in this browser, so next month's report is one click.
 */
import type { ReportRow } from '@/api/data';
import { T } from '@/i18n';
import type { Tenant } from '@/shell/tenant';
import { useState } from 'react';

interface Saved {
  stores: number[];
  /** null is everyone with pay */
  people: string[] | null;
}

const KEY = (t: number) => `timefly.lonn.scope.${t}`;
function load(t: number | undefined): Saved | null {
  if (t == null) return null;
  try {
    const v = JSON.parse(localStorage.getItem(KEY(t)) ?? 'null');
    return v && Array.isArray(v.stores) ? v : null;
  } catch {
    return null;
  }
}
function save(t: number | undefined, v: Saved) {
  if (t == null) return;
  try {
    localStorage.setItem(KEY(t), JSON.stringify(v));
  } catch {
    /* private window: the choice lasts this visit */
  }
}

export interface Scope {
  /** the chosen stores, the active one included unless unticked */
  stores: number[];
  people: string[] | null;
  /** more than the active store: hours come from other stores too */
  multi: boolean;
  /** for GET /admin/report: '' or '&stores=1,4' */
  query: string;
  /** the same, plus the people, for the two files */
  fileQuery: string;
  setStores: (ids: number[]) => void;
  setPeople: (ids: string[] | null) => void;
  /** the rows for the chosen people */
  pick: (rows: ReportRow[]) => ReportRow[];
}

export function useScope(active: number | undefined, tenants: Tenant[]): Scope {
  const [saved, setSaved] = useState<Saved | null>(() => load(active));
  const [forStore, setForStore] = useState(active);
  // another store became active: its own remembered choice
  if (forStore !== active) {
    setForStore(active);
    setSaved(load(active));
  }
  const known = new Set(tenants.map((t) => t.id));
  const stores = (saved?.stores ?? []).filter((id) => known.has(id) || id === active);
  const chosen = stores.length ? stores : active != null ? [active] : [];
  const people = saved?.people ?? null;
  const multi = chosen.length > 1 || (chosen.length === 1 && chosen[0] !== active);
  const query = multi ? `&stores=${chosen.join(',')}` : '';
  const update = (v: Saved) => {
    setSaved(v);
    save(active, v);
  };
  return {
    stores: chosen,
    people,
    multi,
    query,
    fileQuery: `${query}${people ? `&people=${people.join(',')}` : ''}`,
    setStores: (ids) => update({ stores: ids, people }),
    setPeople: (ids) => update({ stores: chosen, people: ids }),
    pick: (rows) => (people ? rows.filter((r) => people.includes(String(r.id))) : rows),
  };
}

/**
 * The stores as ticks, in the side panel "2 bedrifter" opens (manager redesign, 2026-10-09). At least
 * one stays ticked: an empty store list would be the active store again, silently.
 */
export function StoresPick({ scope, tenants }: { scope: Scope; tenants: Tenant[] }) {
  const toggle = (id: number) => {
    const next = scope.stores.includes(id) ? scope.stores.filter((x) => x !== id) : [...scope.stores, id];
    if (next.length) scope.setStores(next);
  };
  return (
    <fieldset className="lpick" data-testid="scope-stores">
      <legend>{T('Bedrifter i lønnsgrunnlaget')}</legend>
      {tenants.map((t) => (
        <label key={t.id}>
          <input
            type="checkbox"
            checked={scope.stores.includes(t.id)}
            disabled={scope.stores.length === 1 && scope.stores.includes(t.id)}
            onChange={() => toggle(t.id)}
          />{' '}
          {t.name}
        </label>
      ))}
      <button type="button" className="mlink" onClick={() => scope.setStores(tenants.map((t) => t.id))}>
        {T('Alle mine bedrifter')}
      </button>
      {scope.multi && (
        <p className="lnote">
          {T(
            'Timene fra alle valgte butikker er lagt sammen per person, og hver vakt er priset etter butikken den ble jobbet i. Overtid telles over uken og dagen i alle valgte butikker, med overtidsregelen til butikken timen ble jobbet i.',
          )}
        </p>
      )}
    </fieldset>
  );
}

/** The people with pay in the chosen stores. None ticked is nobody, on the screen and in the files. */
export function PeoplePick({ scope, rows }: { scope: Scope; rows: ReportRow[] }) {
  const everyone = rows.map((r) => String(r.id));
  const ticked = scope.people ?? everyone;
  const toggle = (id: string) => {
    const next = ticked.includes(id) ? ticked.filter((x) => x !== id) : [...ticked, id];
    scope.setPeople(next.length === everyone.length ? null : next);
  };
  return (
    <fieldset className="lpick" data-testid="scope-people">
      <legend>{T('Ansatte i lønnsgrunnlaget')}</legend>
      <span className="lpickall">
        <button type="button" className="mlink" onClick={() => scope.setPeople(null)}>
          {T('Alle')}
        </button>
        <button type="button" className="mlink" onClick={() => scope.setPeople([])}>
          {T('Ingen')}
        </button>
      </span>
      {rows.length === 0 && <p className="lnote">{T('Ingen har lønn i måneden.')}</p>}
      {rows.map((r) => (
        <label key={r.id}>
          <input
            type="checkbox"
            checked={ticked.includes(String(r.id))}
            onChange={() => toggle(String(r.id))}
          />{' '}
          {r.name}
        </label>
      ))}
    </fieldset>
  );
}
